import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IChatResponse, IrolesResponse, IUserResponse, IUsersResponse, IUsersResponseWithVersion } from '../../core/models/user.model';
import { BenchmarkPortfolio, BenchmarkResponse, BenchmarkCreateRequest, BenchmarkCompanyAddRequest } from '../monitoring/monitoring.model';
import { Observable, map } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AdminPanelService {

  constructor(private http:HttpClient) { }

  getUsers(): Observable<IUsersResponseWithVersion> {
    return this.http.get<IUsersResponse>('/backend/adminPanel/users', {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("current_user_token"),
      },
      responseType: 'json',
      observe: 'response'
    }).pipe(
      map(response => ({
        payload: response.body?.payload || [],
        version: response.headers.get('X-App-Version') || undefined
      }))
    );
  }

  deleteUser(id: number,deleteAPIUser:boolean,api_username:string) {
    const body={
     id:id,
     deleteAPIUser:deleteAPIUser,
     api_username:api_username
     }

    return this.http.post('/backend/adminPanel/users/delete',body, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("current_user_token"),
      },
      reportProgress: true,
      responseType: 'json',
    }
    )
  }

  createUser(formData:any,roles:string[],){
    const body={
      ...formData,
      roles:roles,
     }
    return this.http.post<IUserResponse>('/backend/adminPanel/users/create',body, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("current_user_token"),
      },
      reportProgress: true,
      responseType: 'json',
    }
    )

  }

  downloadUserChat(id: number){
    return this.http.get<IChatResponse>(`/backend/siaAssistant/userData/${id}`, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("current_user_token"),
      },
      responseType: 'json',
    });
  }
  
  updateUser(formData:any,roles:string[],id:number|undefined){
   const body={
    ...formData,
    roles:roles,
    id:id
   }
    return this.http.post<IUserResponse>('/backend/adminPanel/users/update',body, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("current_user_token"),
      },
      reportProgress: true,
      responseType: 'json',
    }
    )
  }
  getAllRoles(){
  
    return this.http.get<IrolesResponse>('/backend/adminPanel/users/roles', {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("current_user_token"),
      },
      reportProgress: true,
      responseType: 'json',
    }
    )
  }

  // Benchmark Portfolio Management Methods
  getAllBenchmarks() {
    return this.http.get<BenchmarkResponse>('/backend/benchmarks', {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("current_user_token"),
      },
      responseType: 'json',
    });
  }

  createBenchmark(benchmarkData: BenchmarkCreateRequest) {
    return this.http.post<BenchmarkResponse>('/backend/benchmarks/create', benchmarkData, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("current_user_token"),
      },
      responseType: 'json',
    });
  }

  updateBenchmark(benchmarkId: number, benchmarkData: BenchmarkCreateRequest) {
    return this.http.post<BenchmarkResponse>(`/backend/benchmarks/update/name/${benchmarkId}`, benchmarkData, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("current_user_token"),
      },
      responseType: 'json',
    });
  }

  deleteBenchmark(benchmarkId: number) {
    return this.http.delete<{ message: string }>(`/backend/benchmarks/${benchmarkId}`, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("current_user_token"),
      },
      responseType: 'json',
    });
  }

  uploadBenchmarkCompanies(benchmarkId: number, companies: BenchmarkCompanyAddRequest[]) {
    return this.http.post<any>(`/backend/benchmarks/${benchmarkId}/companies/bulk`, companies, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("current_user_token"),
      },
      responseType: 'json',
    });
  }

  getBenchmarkCompanies(benchmarkId: number) {
    return this.http.get<any>(`/backend/benchmarks/${benchmarkId}/companies`, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("current_user_token"),
      },
      responseType: 'json',
      observe: 'response'
    });
  }

  deleteBenchmarkCompany(benchmarkId: number, entityId: string) {
    return this.http.delete<{ message: string }>(`/backend/benchmarks/${benchmarkId}/companies/${entityId}`, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("current_user_token"),
      },
      responseType: 'json',
    });
  }

  updateBenchmarkVisibility(benchmarkId: number, isVisible: boolean) {
    return this.http.post<BenchmarkResponse>(`/backend/benchmarks/update/visibility/${benchmarkId}`, { is_visible: isVisible }, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("current_user_token"),
      },
      responseType: 'json',
    });
  }

  reorderBenchmarks(orderedIds: number[]) {
    return this.http.post<{ message: string }>('/backend/benchmarks/reorder', { ordered_ids: orderedIds }, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("current_user_token"),
      },
      responseType: 'json',
    });
  }

  updateBenchmarkDescription(benchmarkId: number, description: string | null) {
    return this.http.post<BenchmarkResponse>(`/backend/benchmarks/update/description/${benchmarkId}`, { description }, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("current_user_token"),
      },
      responseType: 'json',
    });
  }

  // Email Notification Methods

  /**
   * Get users who don't have an email configured
   */
  getUsersWithoutEmail() {
    return this.http.get<any>('/backend/adminPanel/users/without-email', {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("current_user_token"),
      },
      responseType: 'json',
    });
  }

  /**
   * Preview notification email for selected users
   * Each user receives a PERSONALIZED email with only their own watchlists
   * and selected benchmarks they're subscribed to.
   *
   * @param userIds - Array of user IDs to preview for
   * @param includeWatchlists - Whether to include user's own watchlists
   * @param benchmarkIds - Array of benchmark IDs to include (empty = none)
   */
  previewNotification(userIds: number[], includeWatchlists: boolean = true, benchmarkIds: number[] = []) {
    const body = {
      user_ids: userIds,
      include_watchlists: includeWatchlists,
      benchmark_ids: benchmarkIds
    };
    return this.http.post<any>('/backend/adminPanel/notifications/preview', body, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("current_user_token"),
      },
      responseType: 'json',
    });
  }

  /**
   * Send PERSONALIZED notification emails to selected users
   * Each user gets their own email with only their watchlists and subscribed benchmarks
   */
  sendNotification(userIds: number[], subject: string, includeWatchlists: boolean, benchmarkIds: number[]) {
    return this.http.post<any>('/backend/adminPanel/notifications/send', {
      user_ids: userIds,
      subject: subject,
      include_watchlists: includeWatchlists,
      benchmark_ids: benchmarkIds
    }, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("current_user_token"),
      },
      responseType: 'json',
    });
  }
}
